"use client";

/**
 * confirmModal — antd Modal.confirm 스타일의 확인 다이얼로그
 *
 * ConfirmDialog(controlled 컴포넌트)와 달리 open/onClose state 관리 없이 함수 호출 한 번으로 띄운다.
 * 배경이 어두워지고(dim) 뒤 화면 클릭도 막히는 진짜 모달이다 — confirmToast(sonner 기반)와 달리
 * sonner 에 의존하지 않는다.
 *
 * 내부적으로 document.body 에 임시 컨테이너를 만들어 필요할 때만 렌더링하고, 닫히면(애니메이션 끝난 뒤)
 * 스스로 언마운트해서 정리한다 — 앱 루트에 별도 Provider 를 마운트해둘 필요가 없다.
 *
 * @example
 * confirmModal({
 *   title: "제휴사를 삭제하시겠습니까?",
 *   description: "삭제하면 되돌릴 수 없습니다.",
 *   confirmLabel: "삭제",
 *   onConfirm: () => deleteMutation.mutateAsync({ ... }),
 * });
 */
import { type ReactNode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";

import { ConfirmDialog } from "./confirm-dialog";

export interface ConfirmModalOptions {
  /** 다이얼로그 제목 */
  title?: ReactNode;
  /** 제목 아래 보조 설명 */
  description?: ReactNode;
  /** 확인 버튼 라벨 (기본 "확인") */
  confirmLabel?: string;
  /** 취소 버튼 라벨 (기본 "취소") */
  cancelLabel?: string;
  /** 확인 클릭 시 실행. Promise 를 반환하면 완료될 때까지 확인 버튼이 로딩 상태가 되고, 성공해야 닫힌다. */
  onConfirm: () => void | Promise<void>;
  /** 취소 클릭 시 실행 (선택) */
  onCancel?: () => void;
  /** 확인 버튼 색상 (기본 "default" = 프라이머리 색) */
  confirmVariant?: "default" | "destructive";
}

// DialogContent 의 닫힘 애니메이션(duration-200)이 끝난 뒤 언마운트하기 위한 대기 시간
const CLOSE_ANIMATION_MS = 200;

function ConfirmModalRoot({
  options,
  onExitComplete,
}: {
  options: ConfirmModalOptions;
  onExitComplete: () => void;
}) {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) return;
    const timer = setTimeout(onExitComplete, CLOSE_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [open, onExitComplete]);

  return (
    <ConfirmDialog
      open={open}
      loading={loading}
      title={options.title}
      description={options.description}
      confirmLabel={options.confirmLabel}
      cancelLabel={options.cancelLabel}
      confirmVariant={options.confirmVariant}
      onClose={() => {
        setOpen(false);
        options.onCancel?.();
      }}
      onConfirm={async () => {
        try {
          setLoading(true);
          await options.onConfirm();
          setOpen(false);
        } finally {
          setLoading(false);
        }
      }}
    />
  );
}

export function confirmModal(options: ConfirmModalOptions) {
  if (typeof document === "undefined") return;

  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = createRoot(container);

  const dispose = () => {
    root.unmount();
    container.remove();
  };

  root.render(
    <ConfirmModalRoot options={options} onExitComplete={dispose} />
  );
}
